import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientsHealth, type ClientHealth, type HealthScore } from '../../lib/refonteApi';
import { SkeletonCards } from '../../components/refonte/Skeleton';
import MethodHint from '../../components/refonte/MethodHint';
import EmptyState from '../../components/refonte/EmptyState';

const META: Record<HealthScore, { label: string; dot: string; text: string; box: string }> = {
  risque:    { label: 'À risque',    dot: 'bg-st-bad',  text: 'text-st-bad',  box: 'border-st-bad/40 bg-st-bad/5' },
  attention: { label: 'À surveiller', dot: 'bg-st-warn', text: 'text-st-warn', box: 'border-st-warn/40 bg-st-warn/5' },
  ok:        { label: 'Solide',      dot: 'bg-st-ok',   text: 'text-st-ok',   box: 'border-line bg-panel' },
};
const ORDER: HealthScore[] = ['risque', 'attention', 'ok'];

const KpiTile: React.FC<{ v: React.ReactNode; l: string; tone?: string }> = ({ v, l, tone }) => (
  <div className="bg-panel border border-line rounded-card p-4">
    <div className={`text-[26px] font-bold font-mono tabular-nums leading-none ${tone || ''}`}>{v}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{l}</div>
  </div>
);

// Santé clients (CSM/Manager) : alertes delivery/facturation en tête, puis clients par niveau
// de risque. Chaque carte ouvre la fiche client (cockpit). Version portefeuille du cockpit.
const CsmHealthPage: React.FC = () => {
  const nav = useNavigate();
  const [allRows, setAllRows] = useState<ClientHealth[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => { setLoading(true); try { setAllRows(await getClientsHealth()); } catch (e: any) { setErr(e.message); } setLoading(false); })();
  }, []);

  // Portefeuille actif par défaut : uniquement les clients ACTIFS ce mois (retour Kevin 25/09 :
  // sinon les terminés/dormants polluent « à risque / à surveiller / solide »). « Voir tous » = tout.
  const rows = showAll ? allRows : allRows.filter(r => r.active);
  const actifs = rows.filter(r => r.active);
  const nbRisque = rows.filter(r => r.score === 'risque').length;
  const alertesClients = rows.filter(r => (r.alerts?.length ?? 0) > 0);
  const renew = rows.filter(r => r.daysToEnd != null && r.daysToEnd >= 0 && r.daysToEnd <= 60);

  const Card: React.FC<{ c: ClientHealth }> = ({ c }) => {
    const m = META[c.score];
    return (
      <button onClick={() => nav(`/app/clients/${c.id}`)} className={`text-left rounded-card border p-4 hover:border-hook-rose transition-colors w-full ${m.box}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0"><div className="text-[14px] font-bold truncate">{c.name}</div><div className="font-mono text-[9px] uppercase tracking-widest text-faint">{c.active ? 'actif' : 'inactif'} · {c.production === 'ok' ? 'en prod' : c.production === 'stale' ? 'inactif 30j' : 'jamais'}</div></div>
          <span className={`inline-flex items-center gap-1.5 font-mono text-[9px] font-semibold px-2 py-0.5 rounded-md uppercase ${m.text}`}><span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />{m.label}</span>
        </div>
        <div className="mt-2 flex gap-3 font-mono text-[11px] text-dim">
          <span>{c.honores ?? 0} honorés<MethodHint text="Honoré = le RDV a eu lieu (statuts honoré, qualifié, non qualifié, validé, contesté, facturé). Tout l'historique du client." /></span>
          <span>{c.qualifies ?? 0} qualif.<MethodHint text="Qualifié = honoré ET conforme au Sales Book (statuts qualifié, validé, facturé)." /></span>
          <span>{c.noShow ?? 0} no-show<MethodHint text="Le prospect n'est pas venu. Un RDV annulé n'est pas un no-show." /></span>
        </div>
        {(c.alerts?.length || c.flags.length) ? (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {(c.alerts ?? []).map(a => <li key={a} className="font-mono text-[9px] text-st-bad bg-st-bad/10 rounded px-1.5 py-0.5">⚠ {a.split(' : ')[0]}</li>)}
            {c.flags.map(fl => <li key={fl} className="font-mono text-[9px] text-dim bg-raise rounded px-1.5 py-0.5">{fl}</li>)}
          </ul>
        ) : null}
      </button>
    );
  };

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-[21px] font-bold tracking-tight">Santé clients</h1>
        <button onClick={() => setShowAll(v => !v)} className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md border border-line-2 text-dim hover:border-ink">{showAll ? 'Clients actifs' : `Voir tous (${allRows.length})`}</button>
      </div>
      <p className="text-dim text-[13px] mb-4">{showAll ? 'Tous les clients.' : 'Clients actifs ce mois.'} Qui est à risque, qui n'est pas produit, qui arrive à renouvellement. Clique un client pour sa fiche.</p>

      {err && <EmptyState tone="error" title="Impossible de charger la santé" hint={err} className="mb-4" />}
      {loading ? <SkeletonCards count={6} /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <KpiTile v={actifs.length} l="Clients actifs" />
            <KpiTile v={nbRisque} l="À risque" tone={nbRisque ? 'text-st-bad' : ''} />
            <KpiTile v={alertesClients.length} l="Alertes delivery/factu" tone={alertesClients.length ? 'text-st-warn' : ''} />
            <KpiTile v={renew.length} l="Renouvellements < 60j" tone={renew.length ? 'text-hook-rose' : ''} />
          </div>

          {/* Alertes actionnables (2×2 cerveau) */}
          {alertesClients.length > 0 && (
            <div className="bg-st-warn/5 border border-st-warn/30 rounded-card p-4 mb-5">
              <h2 className="font-mono text-[10px] uppercase tracking-widest text-st-warn mb-2">⚠ Alertes à traiter</h2>
              <div className="space-y-1.5">
                {alertesClients.map(c => (
                  <button key={c.id} onClick={() => nav(`/app/clients/${c.id}`)} className="w-full text-left flex items-center gap-2 text-[12.5px] hover:text-hook-rose">
                    <span className="font-semibold w-40 truncate shrink-0">{c.name}</span>
                    <span className="text-dim">{c.alerts!.join(' · ')}</span>
                    <span className="ml-auto font-mono text-[11px] text-faint">›</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {rows.length === 0 ? <EmptyState title="Aucun client" hint="Les clients apparaîtront ici." /> : (
            <div className="space-y-5">
              {ORDER.map(sc => {
                const list = rows.filter(r => r.score === sc);
                if (!list.length) return null;
                return (
                  <section key={sc}>
                    <h2 className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${META[sc].text}`}>{META[sc].label} · {list.length}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{list.map(c => <Card key={c.id} c={c} />)}</div>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CsmHealthPage;
